import { useMemo, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Progress } from '@/components/atoms/progress';
import { Switch } from '@/components/atoms/switch';
import { Skeleton } from '@/components/atoms/skeleton';
import { Form } from '@/components/molecules/form-field';

import { useCountries } from '@/lib/hooks/use-countries';
import { useShippingRate } from '@/features/shipping/hooks/use-shipping-rate';
import { useUpdateShippingRate } from '@/features/shipping/hooks/use-update-shipping-rate';
import {
  ShippingRateBasicInfo,
  ShippingRateTypeSelector,
  ShippingRatePricing,
  ShippingRateWeightRanges,
  ShippingRatePriceRanges,
  ShippingRateDeliverySettings,
  ShippingRateCountries,
} from '@/features/shipping/components';
import {
  shippingRateSchema,
  DEFAULT_SHIPPING_RATE_VALUES,
  mapServerCurrencyToForm,
  type ShippingRateFormData,
} from '@/features/shipping/types/shipping-form.types';
import type { UpdateShippingRateData } from '@/features/shipping/types/shipping.types';

export default function EditShippingRatePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { data: shippingRate, isLoading: isLoadingRate, error: loadError } = useShippingRate(id ?? '');
  const { data: countries, isLoading: isLoadingCountries } = useCountries();
  const { mutate: updateShippingRate, isPending: isSubmitting } = useUpdateShippingRate();

  const form = useForm<ShippingRateFormData>({
    resolver: zodResolver(shippingRateSchema),
    defaultValues: {
      ...DEFAULT_SHIPPING_RATE_VALUES,
      isActive: true,
    },
  });

  // Populate form with existing data
  useEffect(() => {
    if (shippingRate) {
      const rateType = shippingRate.rateCalculation?.type || 'flat';
      const hasDeliverySettings = !!(shippingRate.estimatedDeliveryDays || shippingRate.maxDeliveryDays);
      const hasFreeShippingThreshold = !!shippingRate.freeShippingThreshold;

      form.reset({
        name: shippingRate.name,
        description: shippingRate.description || '',
        rateType,
        deliveryType: shippingRate.deliveryType,
        currency: mapServerCurrencyToForm(shippingRate.currency),
        price: parseFloat(shippingRate.price) || 0,
        availableCountries: shippingRate.availableCountries || [],
        freeShippingEnabled: hasFreeShippingThreshold,
        freeShippingThreshold: shippingRate.freeShippingThreshold
          ? parseFloat(shippingRate.freeShippingThreshold)
          : undefined,
        deliverySettingsEnabled: hasDeliverySettings,
        estimatedDeliveryDays: shippingRate.estimatedDeliveryDays || 1,
        maxDeliveryDays: shippingRate.maxDeliveryDays || 3,
        weightUnit: (shippingRate.rateCalculation?.weightUnit as 'kilograms' | 'grams') || 'kilograms',
        weightRanges: shippingRate.rateCalculation?.weightRanges?.length
          ? shippingRate.rateCalculation.weightRanges
          : [{ minWeight: 0, maxWeight: 0, rate: 0 }],
        priceRanges: shippingRate.rateCalculation?.priceRanges?.length
          ? shippingRate.rateCalculation.priceRanges
          : [{ minPrice: 0, maxPrice: 0, rate: 0 }],
        isActive: shippingRate.isActive,
      });
    }
  }, [shippingRate, form]);

  const weightRangesFieldArray = useFieldArray({ control: form.control, name: 'weightRanges' });
  const priceRangesFieldArray = useFieldArray({ control: form.control, name: 'priceRanges' });

  const watchRateType = form.watch('rateType');
  const watchDeliveryType = form.watch('deliveryType');
  const watchIsActive = form.watch('isActive');

  const isPickup = watchDeliveryType === 'pickup';
  const isFreeShipping = watchRateType === 'free_shipping';
  const showFlatPricing = watchRateType === 'flat' && !isPickup && !isFreeShipping;

  // Calculate form progress
  const formValues = form.watch();
  const { progress, completedFields, totalFields } = useMemo(() => {
    const requiredFields = ['name', 'rateType', 'deliveryType', 'currency', 'availableCountries'];

    let completed = 0;
    if (formValues.name?.length >= 2) completed++;
    if (formValues.rateType) completed++;
    if (formValues.deliveryType) completed++;
    if (formValues.currency) completed++;
    if (formValues.availableCountries?.length > 0) completed++;

    if (!isFreeShipping && !isPickup) {
      if (formValues.price > 0) completed++;
    }

    const total = isFreeShipping || isPickup ? requiredFields.length : requiredFields.length + 1;

    return {
      completedFields: completed,
      totalFields: total,
      progress: (completed / total) * 100,
    };
  }, [formValues, isFreeShipping, isPickup]);

  const onSubmit = async (data: ShippingRateFormData) => {
    if (!id) return;

    let price = data.price;
    if (data.rateType === 'free_shipping' || isPickup) {
      price = 0;
    }

    let rateCalculation: UpdateShippingRateData['rateCalculation'] | undefined;

    if (data.rateType === 'weight_based' && data.weightRanges) {
      const validRanges = data.weightRanges.filter(
        (r) => r.minWeight > 0 || r.maxWeight > 0 || r.rate > 0
      );
      if (validRanges.length > 0) {
        rateCalculation = {
          type: data.rateType,
          weightUnit: data.weightUnit,
          weightRanges: validRanges,
        };
      }
    } else if (data.rateType === 'price_based' && data.priceRanges) {
      const validRanges = data.priceRanges.filter(
        (r) => r.minPrice > 0 || r.maxPrice > 0 || r.rate > 0
      );
      if (validRanges.length > 0) {
        rateCalculation = {
          type: data.rateType,
          priceRanges: validRanges,
        };
      }
    } else if (data.rateType === 'flat') {
      rateCalculation = { type: 'flat', baseRate: price };
    } else {
      rateCalculation = { type: data.rateType };
    }

    const payload: UpdateShippingRateData = {
      name: data.name,
      description: data.description || undefined,
      deliveryType: data.deliveryType,
      price,
      currency: data.currency,
      availableCountries: data.availableCountries,
      isActive: data.isActive ?? true,
    };

    if (rateCalculation) {
      payload.rateCalculation = rateCalculation;
    }

    if (data.freeShippingEnabled && data.freeShippingThreshold) {
      payload.freeShippingThreshold = data.freeShippingThreshold;
    }

    if (data.deliverySettingsEnabled) {
      if (data.estimatedDeliveryDays) payload.estimatedDeliveryDays = data.estimatedDeliveryDays;
      if (data.maxDeliveryDays) payload.maxDeliveryDays = data.maxDeliveryDays;
    }

    updateShippingRate({ refId: id, data: payload });
  };

  const onFormError = useCallback((errors: Record<string, unknown>) => {
    console.error('Form validation errors:', errors);
    toast.error(t('validation.formErrors', { defaultValue: 'Please fix the form errors' }));
  }, [t]);

  if (isLoadingRate) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent className="pt-6">
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Skeleton className="h-48 w-full" />
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loadError || !shippingRate) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('shipping.error')}</p>
      </div>
    );
  }

  return (
    <div className="pb-6 w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center mb-6">
        <Button variant="ghost" size="icon" asChild className="shrink-0 self-start sm:self-auto">
          <Link to={`/shipping/rates/${id}`}>
            <ArrowLeft className="size-5" />
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold truncate">{t('shipping.editRate', { defaultValue: 'Edit Shipping Rate' })}</h1>
          <p className="text-muted-foreground truncate">{shippingRate.name}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm text-muted-foreground">{t('common.active')}</span>
          <Switch
            checked={watchIsActive}
            onCheckedChange={(checked) => form.setValue('isActive', checked)}
          />
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="mb-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex justify-between text-sm mb-2">
          <span>
            {completedFields}/{totalFields} {t('products.fieldsCompleted', { defaultValue: 'fields completed' })}
          </span>
          <span>{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onFormError)}>
          <div className="flex flex-col gap-6 lg:grid lg:grid-cols-3">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-6">
              <ShippingRateBasicInfo form={form} />
              <ShippingRateTypeSelector form={form} />
              <ShippingRatePricing form={form} showPricing={showFlatPricing} isPickup={isPickup} />

              {watchRateType === 'weight_based' && !isPickup && (
                <ShippingRateWeightRanges form={form} fieldArray={weightRangesFieldArray} />
              )}

              {watchRateType === 'price_based' && !isPickup && (
                <ShippingRatePriceRanges form={form} fieldArray={priceRangesFieldArray} />
              )}

              <ShippingRateDeliverySettings form={form} />
            </div>

            {/* Right Column - Countries & Submit */}
            <div className="space-y-6">
              <ShippingRateCountries
                form={form}
                countries={countries}
                isLoading={isLoadingCountries}
              />

              {/* Submit Button */}
              <div className="sticky bottom-4">
                <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {t('shipping.updateRate', { defaultValue: 'Update Shipping Rate' })}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
